import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { doc, setDoc } from 'firebase/firestore';
import { db, messaging } from './firebase';
import { getToken, onMessage } from 'firebase/messaging';

const VAPID_KEY = 'BHkhPT0-xg-4RzmXwA8Had3epmvoJWQIO8CE6zV5XJicY2yxRZJIAu5yRD9re4VR6fH100wZTvnVHdi_7bgY-aw';

/** Guarda el token FCM bajo uid e id de Firestore (el creadorId de la rifa puede ser cualquiera). */
export async function guardarTokenFcmEnFirestore(user, token, platform = 'web') {
  if (!user?.email || !token) return;

  const ids = [...new Set([user.uid, user.id].filter(Boolean))];
  const base = {
    userId: user.id,
    uid: user.uid || user.id,
    email: user.email,
    token,
    platform,
    updatedAt: new Date().toISOString(),
  };

  await Promise.all(
    ids.map((id) =>
      setDoc(doc(db, 'fcm_tokens', id), { ...base, docKey: id }, { merge: true })
    )
  );
}

function manejarClickNotificacion(data, onNavigate) {
  if (!onNavigate || !data) return;
  if (data.tipo === 'pago_pendiente') {
    onNavigate('/mis-rifas');
  } else if (data.rifaId) {
    onNavigate(`/rifa/${data.rifaId}`);
  }
}

let nativeListenersRegistrados = false;
let usuarioPushActual = null;

async function registrarPushNativo(user, onNavigate) {
  usuarioPushActual = user;
  if (Capacitor.getPlatform() === 'android') {
    try {
      await PushNotifications.createChannel({
        id: 'pagos_pendientes',
        name: 'Pagos pendientes',
        description: 'Aviso cuando un cliente reserva un número para confirmar',
        importance: 5,
        visibility: 1,
        vibration: true,
      });
    } catch (e) {
      console.warn('Canal de notificaciones:', e?.message);
    }
  }

  if (!nativeListenersRegistrados) {
    nativeListenersRegistrados = true;

    PushNotifications.addListener('registration', async (ev) => {
      const token = ev?.value;
      const u = usuarioPushActual;
      if (token && u) {
        console.log('✅ Token push nativo:', token.substring(0, 24) + '...');
        await guardarTokenFcmEnFirestore(u, token, Capacitor.getPlatform());
      }
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.error('Error registro push nativo:', err);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('📩 Push recibida (app abierta):', notification);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification?.data || {};
      manejarClickNotificacion(data, onNavigate);
    });
  }

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === 'prompt') {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== 'granted') {
    console.log('Permiso de notificaciones denegado en el dispositivo');
    return;
  }

  await PushNotifications.register();
}

async function registrarPushWeb(user, onNavigate) {
  if (!messaging || !('serviceWorker' in navigator)) return null;

  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });

  if (token) {
    await guardarTokenFcmEnFirestore(user, token, 'web');
  }

  return onMessage(messaging, (payload) => {
    if (!payload.notification) return;
    const notification = new Notification(payload.notification.title, {
      body: payload.notification.body,
      icon: payload.notification.icon || '/logo.png',
      badge: '/logo.png',
      tag: payload.data?.tag || 'notificacion',
      data: payload.data || {},
    });
    notification.onclick = () => {
      window.focus();
      manejarClickNotificacion(payload.data, onNavigate);
      notification.close();
    };
  });
}

/**
 * Registra push en Android/iOS (Capacitor) o web (FCM + service worker).
 * @returns función de limpieza (solo web)
 */
export async function iniciarNotificacionesPush(user, onNavigate) {
  if (!user) {
    usuarioPushActual = null;
    return () => {};
  }

  usuarioPushActual = user;

  try {
    if (Capacitor.isNativePlatform()) {
      await registrarPushNativo(user, onNavigate);
      return () => {};
    }
    const unsub = await registrarPushWeb(user, onNavigate);
    return typeof unsub === 'function' ? unsub : () => {};
  } catch (error) {
    console.error('Error al iniciar notificaciones push:', error);
    return () => {};
  }
}
